import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { submitLead } from "@/lib/api";
import { SERVICES } from "@/lib/site";

const RELATIONSHIPS = [
  "Myself",
  "Parent",
  "Spouse",
  "Grandparent",
  "Other family member",
  "Friend / Neighbor",
];

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    relationship: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in name, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        relationship: form.relationship || null,
        service_interest: form.service_interest || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Thank you — we’ll be in touch within one business day.");
      setForm({
        name: "",
        phone: "",
        email: "",
        relationship: "",
        service_interest: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Unable to submit right now. Please call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-form-success"
        className="rounded-2xl border border-gold-200 bg-gold-50 p-10 text-center"
      >
        <h3 className="font-serif text-3xl text-navy-900">Message received</h3>
        <p className="mt-3 text-navy-700">
          A member of our care team will reach out within one business day.
          For urgent needs, please call{" "}
          <a
            href="tel:+12398344377"
            className="font-medium underline underline-offset-4"
          >
            (239) 834-4377
          </a>
          .
        </p>
        <button
          type="button"
          data-testid="contact-form-reset"
          onClick={() => setDone(false)}
          className="btn-secondary mt-6"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={onSubmit}
      className={`rounded-2xl bg-white border border-navy-900/10 shadow-sm p-7 md:p-10 ${
        compact ? "" : "md:p-12"
      }`}
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *" id="lead-name">
          <input
            id="lead-name"
            data-testid="contact-input-name"
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            className="form-input"
            placeholder="Jane Doe"
            autoComplete="name"
          />
        </Field>
        <Field label="Phone *" id="lead-phone">
          <input
            id="lead-phone"
            data-testid="contact-input-phone"
            type="tel"
            required
            value={form.phone}
            onChange={update("phone")}
            className="form-input"
            placeholder="(239) 834-4377"
            autoComplete="tel"
          />
        </Field>
        <Field label="Email *" id="lead-email" className="md:col-span-2">
          <input
            id="lead-email"
            data-testid="contact-input-email"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            className="form-input"
            placeholder="jane@example.com"
            autoComplete="email"
          />
        </Field>
        <Field label="Care is for" id="lead-relationship">
          <select
            id="lead-relationship"
            data-testid="contact-select-relationship"
            value={form.relationship}
            onChange={update("relationship")}
            className="form-input bg-white"
          >
            <option value="">Select…</option>
            {RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service of interest" id="lead-service">
          <select
            id="lead-service"
            data-testid="contact-select-service"
            value={form.service_interest}
            onChange={update("service_interest")}
            className="form-input bg-white"
          >
            <option value="">Select…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>
                {s.title}
              </option>
            ))}
            <option value="Not sure yet">Not sure yet</option>
          </select>
        </Field>
        <Field label="How can we help?" id="lead-message" className="md:col-span-2">
          <textarea
            id="lead-message"
            data-testid="contact-input-message"
            rows={5}
            value={form.message}
            onChange={update("message")}
            className="form-input resize-none"
            placeholder="Tell us a little about your loved one and the kind of care you're looking for."
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-form-submit"
        className="btn-primary w-full md:w-auto mt-8"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" strokeWidth={1.75} /> Request my free
            consultation
          </>
        )}
      </button>
      <p className="mt-4 text-sm text-navy-500">
        Your information is kept strictly confidential. No obligation.
      </p>

      <style>{`
        .form-input {
          width: 100%;
          border: 1px solid rgb(226 232 240);
          border-radius: 12px;
          padding: 14px 16px;
          color: #0A192F;
          background: #ffffff;
          font-size: 15px;
          transition: border-color .2s, box-shadow .2s;
          outline: none;
        }
        .form-input::placeholder { color: #94a3b8; }
        .form-input:focus {
          border-color: #D4AF37;
          box-shadow: 0 0 0 4px rgba(212,175,55,0.15);
        }
      `}</style>
    </form>
  );
}

function Field({ label, id, children, className = "" }) {
  return (
    <label htmlFor={id} className={`flex flex-col gap-2 ${className}`}>
      <span className="text-sm font-medium text-navy-800">{label}</span>
      {children}
    </label>
  );
}
